import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdownField, DateRangePicker, DropdownList, FlexRow, Icon, Tooltip} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {BookmarkIcon, BOOKMARK_COLOR_DISABLED} from '../../shared/icons/BookmarkIcon';

/* the design: shows the date-range placeholder uppercase (DD/MM/YYYY). The DS
 * DateRangePicker's react-aria segments render lowercase (mm/dd/yyyy) with no format prop;
 * text-transform inherits into the segment spans, uppercasing the placeholder to MM/DD/YYYY
 * without forking the component. Segment ORDER stays locale-driven (mm/dd for en-US), which is
 * correct per-locale — only the case is normalised to match the design. */
const UppercaseDateWrapper = styled.div`
	text-transform: uppercase;
`;

type DatePickerDate = string | null;

interface DateRangeChangeEvent {
	startDate: DatePickerDate;
	endDate: DatePickerDate;
}

/** Visual-only period presets (the design show a single "Previous month"
 * preset trigger — no other preset labels are visible in the design, so this list is
 * intentionally the minimal set the screenshots evidence). Selecting a preset is local state only
 * — no filtering/query behaviour wired up in this ticket (period logic is out of scope, plan Task
 * 8). */
const PERIOD_PRESET_KEYS = [
	'ai_report_builder.period_preset.previous_month',
	'ai_report_builder.period_preset.this_month',
	'ai_report_builder.period_preset.this_quarter',
] as const;

/** Top bar shown in edit mode: a period-preset dropdown (visual-only, local state) followed by a
 * separate date-range field (the design shows these as two adjacent but visually distinct controls, not
 * one merged widget), a disabled single-button Download (neutral field style with an inline
 * chevron) / Share (stubbed with a tooltip; real behaviour lands in a later ticket), and a
 * Save Report button wired to `useReportBuilder().save()`. The report name itself now lives in
 * `ReportPageHeader` (rendered above this bar in `ReportBuilderSurface`), not here. */
export function EditTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {dirty, save, setMode} = useReportBuilder();

	// Visual-only period state — no filtering/query behaviour wired up in this ticket.
	const [periodPreset, setPeriodPreset] = useState<string>(PERIOD_PRESET_KEYS[0]);
	const [startDate, setStartDate] = useState<DatePickerDate>(null);
	const [endDate, setEndDate] = useState<DatePickerDate>(null);

	const handlePeriodChange = (range: DateRangeChangeEvent): void => {
		setStartDate(range.startDate);
		setEndDate(range.endDate);
	};

	// Per the design, Save Report both persists the draft AND returns the surface to view mode (it does
	// not just persist and stay in edit mode).
	const handleSave = (): void => {
		void save().then(() => setMode('view'));
	};

	const stubTooltip = formatMessage({id: 'ai_report_builder.stub_action_tooltip'});

	return (
		<FlexRow justifyContent={'space-between'} alignItems={'center'} data-cy={'ai-report-edit-top-bar'}>
			{/* the design Toolbar is SPACE_BETWEEN: Left Controls (preset + date, itemSpacing 8) hug the
			 * left, Right Controls (actions) hug the right. DS FlexRow defaults to flex:1, so both
			 * groups would each take 50% and the date/actions would drift to mid-row; pin both to
			 * flex:0 0 auto so they hug content and space-between pushes them to the true edges. */}
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<DropdownList>
					<DropdownList.Trigger>
						{/* Neutral field-style dropdown (bordered, dark #232334 text on a neutral-1
						 * background) per the design, matching the adjacent date-range field —
						 * NOT a blue high/medium-emphasis button. `ButtonDropdownField` is the DS
						 * field-variant trigger (see GroupByControl.tsx, which uses the same for its
						 * "Group By" field). Pinned to 180px (the design) instead of the
						 * component default (100%), which would stretch it across the flex row. */}
						<ButtonDropdownField
							width={'180px'}
							data-cy={'ai-report-period-preset'}
							aria-label={formatMessage({id: 'ai_report_builder.period_preset'})}
						>
							<FormattedMessage id={periodPreset} />
						</ButtonDropdownField>
					</DropdownList.Trigger>
					<DropdownList.Content offset={4}>
						{PERIOD_PRESET_KEYS.map(key => (
							<DropdownList.Item
								key={key}
								onClick={() => setPeriodPreset(key)}
								data-cy={`ai-report-period-preset-option-${key}`}
							>
								<FormattedMessage id={key} />
							</DropdownList.Item>
						))}
					</DropdownList.Content>
				</DropdownList>
				<UppercaseDateWrapper>
					<DateRangePicker
						aria-label={formatMessage({id: 'ai_report_builder.period'})}
						data-cy={'ai-report-period-picker'}
						startDate={startDate}
						endDate={endDate}
						onChange={handlePeriodChange}
					/>
				</UppercaseDateWrapper>
			</FlexRow>
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<Tooltip content={stubTooltip}>
					<span>
						{/* the design: Download is a SINGLE neutral field-style button — leading
						 * download glyph, "Download" label, inline trailing chevron — NOT a split button
						 * with a detached caret section (which the design does not have). Rendered
						 * disabled (stub; real download behaviour lands in a later ticket).
						 * ButtonDropdownField supplies the neutral border + built-in trailing chevron. */}
						<ButtonDropdownField
							width={'auto'}
							disabled
							aria-label={formatMessage({id: 'ai_report_builder.download'})}
							data-cy={'ai-report-download-button'}
						>
							<FlexRow alignItems={'center'} gap={'xs'}>
								<Icon icon={'download'} size={'s'} />
								<FormattedMessage id={'ai_report_builder.download'} />
							</FlexRow>
						</ButtonDropdownField>
					</span>
				</Tooltip>
				<Tooltip content={stubTooltip}>
					<span>
						<Button
							emphasis={'medium'}
							icon={'share'}
							disabled
							data-cy={'ai-report-share-button'}
							aria-label={formatMessage({id: 'ai_report_builder.share'})}
						>
							{formatMessage({id: 'ai_report_builder.share'})}
						</Button>
					</span>
				</Tooltip>
				<Button emphasis={'high'} disabled={!dirty} onPress={handleSave} data-cy={'ai-report-save-button'}>
					{/* the design: Save Report carries a leading bookmark glyph. The DS icon set
					 * has no bookmark; reuse the shared BookmarkIcon (shared/icons/BookmarkIcon) that
					 * the agent chat also uses for "Your Saved Prompts". White on the enabled dark-blue high-emphasis
					 * button; the muted token when disabled (clean) so it reads with the greyed label. */}
					<FlexRow alignItems={'center'} gap={'xs'}>
						<BookmarkIcon color={dirty ? '#ffffff' : BOOKMARK_COLOR_DISABLED} />
						<FormattedMessage id={'ai_report_builder.save_report'} />
					</FlexRow>
				</Button>
			</FlexRow>
		</FlexRow>
	);
}
